/*
* Product Customizations Feature
* 
* Dedicated JavaScript for product customization functionality
* Includes modal interface, pricing logic, and cart edit functionality
*/

(function() {
  'use strict';
  
  console.log('🎨 PRODUCT CUSTOMIZATIONS LOADED - Version:', new Date().toISOString());
  
  // All the product customizations code goes here (extracted from custom.js lines 17-2431)
  // This allows custom.js to remain clean for other general customizations

  /**
   * Product Customization Component (Modal Version)
   * Allows customers to add customization requests with pricing logic:
   * - First customization is free
   * - Additional customizations are charged with fixed currency pricing
   */
  
  if (!customElements.get('product-customizations')) {
    customElements.define('product-customizations', class extends HTMLElement {
      constructor() {
        super();
        this.customizations = [];
        this.freeCount = parseInt(this.dataset.freeCount || '1');
        this.chargeAmount = parseFloat(this.dataset.chargeAmount || '10.00');
        this.chargeAmountCents = parseInt(this.dataset.chargeAmountCents || '1000');
        this.maxCustomizations = parseInt(this.dataset.maxCustomizations || '3');
        this.feeVariantId = this.dataset.feeVariantId || '56012001018235';
        this.isDress = this.dataset.isDress === 'true';
        this.currencyCode = this.dataset.currencyCode || 'USD';
        this.currencySymbol = this.dataset.currencySymbol || '$';
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        
        // Get product tags to determine which choice options to show
        this.productTags = this.dataset.productTags ? this.dataset.productTags.split(',') : [];
        
        // Track saved choice selections
        this.savedChoices = {};
      }

      connectedCallback() {
        // Find form more reliably
        this.form = this.closest('form[data-product-form]') || 
                    this.closest('product-form')?.querySelector('form') ||
                    document.querySelector('form[data-product-form]');
        this.modal = this.querySelector('dialog');
        this.trigger = this.querySelector('[data-customizations-trigger]');
        this.triggerText = this.querySelector('[data-trigger-text]');
        this.triggerCount = this.querySelector('[data-trigger-count]');
        this.addButton = this.querySelector('[data-add-customization]');
        this.container = this.querySelector('[data-customizations-container]');
        this.totalEl = this.querySelector('[data-customization-total]');
        this.totalAmount = this.querySelector('[data-total-amount]');
        this.hiddenInput = this.querySelector('[data-customization-data]');
        this.applyButton = this.querySelector('[data-apply-customizations]');
        this.statusMessage = this.querySelector('[data-status-message]');
        this.modalStatusMessage = this.querySelector('[data-modal-status-message]');
        this.messageEl = this.querySelector('[data-customizations-message]');
        this.choicesContainer = this.querySelector('[data-customization-choices]');
        this.editingCartItemKey = null;
        this.editingVariantId = null;
        
        console.log('[Init] statusMessage:', this.statusMessage);
        console.log('[Init] modalStatusMessage:', this.modalStatusMessage);
        
        if (!this.form) {
          console.warn('Product customizations: Missing product form', this);
        }

        this.renderChoiceFields();
        this.bindEvents();
        this.updateUI();
        
        // Check if editing from cart
        this.checkForEditMode();
        
        // Watch for variant price changes on the ATC button
        this.observeButtonPriceChanges();
      }

      // Get fixed pricing for customer's currency
      // Fixed fees per currency, no exchange rate conversion
      getFixedPrice(tier) {
        // tier 2 = 2 customizations total, tier 3 = 3rd customization only (incremental)
        
        const pricing = {
          // Dress Products
          dress: {
            USD: { tier2: 10.00, tier3: 8.00 },
            CAD: { tier2: 14.00, tier3: 11.00 },
            EUR: { tier2: 9.00, tier3: 7.00 },
            GBP: { tier2: 8.00, tier3: 6.00 },
            AUD: { tier2: 15.00, tier3: 12.00 },
            NOK: { tier2: 100.00, tier3: 80.00 },
            SGD: { tier2: 14.00, tier3: 11.00 },
            CHF: { tier2: 9.00, tier3: 7.00 }
          },
          // Other Products (non-dress)
          other: {
            USD: { tier2: 8.00, tier3: 4.00 },
            CAD: { tier2: 11.00, tier3: 5.00 },
            EUR: { tier2: 7.00, tier3: 4.00 },
            GBP: { tier2: 6.00, tier3: 3.00 },
            AUD: { tier2: 12.00, tier3: 6.00 },
            NOK: { tier2: 80.00, tier3: 40.00 },
            SGD: { tier2: 11.00, tier3: 5.00 },
            CHF: { tier2: 7.00, tier3: 4.00 }
          }
        };
        
        const productType = this.isDress ? 'dress' : 'other';
        const currencyPricing = pricing[productType][this.currencyCode];
        
        // If currency not found, fall back to USD
        if (!currencyPricing) {
          console.warn(`Currency ${this.currencyCode} not found, falling back to USD`);
          return pricing[productType].USD[tier];
        }
        
        return currencyPricing[tier];
      }
      
      // Watch for changes to the button price (when variants change)
      observeButtonPriceChanges() {
        const addToCartButton = document.querySelector('[data-add-to-cart]');
        if (!addToCartButton) return;
        
        const priceElement = addToCartButton.querySelector('[data-product-price]');
        if (!priceElement) return;
        
        // Create a MutationObserver to watch for attribute changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-price-cents') {
              // Price changed due to variant selection - recalculate total
              this.updateAddToCartTotal();
            }
          });
        });
        
        // Start observing the price element
        observer.observe(priceElement, {
          attributes: true,
          attributeFilter: ['data-price-cents']
        });
      }

      checkForEditMode() {
        // Check if URL has edit_cart_item parameter
        const urlParams = new URLSearchParams(window.location.search);
        const editCartItemKey = urlParams.get('edit_cart_item');
        
        console.log('🔍 checkForEditMode:', { editCartItemKey });
        
        if (!editCartItemKey) return;
        
        // Wait for page to fully load
        setTimeout(() => {
          console.log('⏰ Fetching cart data...');
          // Load cart item and pre-fill modal
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              console.log('📦 Cart loaded:', cart);
              const item = cart.items.find(i => i.key === editCartItemKey);
              console.log('🎯 Found item:', item);
              
              if (!item) {
                console.warn('Cart item not found:', editCartItemKey);
                return;
              }
              
              // Store for replacement
              this.editingCartItemKey = editCartItemKey;
              this.editingVariantId = item.variant_id;
              
              // Extract customizations
              this.customizations = [];
              Object.keys(item.properties || {}).forEach(key => {
                if (key.startsWith('Customization ')) {
                  this.customizations.push({
                    text: item.properties[key],
                    price: 0
                  });
                }
                // Save choice selections (Closures, Waist Ties, Pockets)
                if (key === 'Closures' || key === 'Waist Ties' || key === 'Pockets') {
                  this.setSavedChoiceValue(key, item.properties[key]);
                }
              });
              
              console.log('✏️ Customizations extracted:', this.customizations);
              console.log('✏️ Customizations count:', this.customizations.length);
              
              // Render choice fields first (to populate saved values)
              this.renderChoiceFields();
              
              // Render and open modal
              this.container.innerHTML = '';
              this.customizations.forEach((c, i) => this.renderCustomization(c, i));
              this.updateUI();
              this.updateTrigger();
              console.log('✏️ Before updateStatusMessage, count:', this.customizations.length);
              this.updateStatusMessage(); // Show price breakdown in modal
              
              console.log('🎭 Opening modal... trigger:', this.trigger);
              
              // Use trigger button to open (ensures proper initialization)
              if (this.trigger) {
                this.trigger.click();
                console.log('✅ Clicked trigger button');
              } else if (this.modal) {
                this.modal.showModal();
                // Ensure modal is interactive
                this.modal.style.pointerEvents = 'auto';
                this.modal.style.zIndex = '9999';
                console.log('✅ Opened modal directly');
              }
              
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
            })
            .catch(err => {
              console.error('Failed to load cart for editing:', err);
            });
        }, 300);
      }

      bindEvents() {
        // Add customization button inside modal (free)
        this.addButton?.addEventListener('click', (e) => {
          e.preventDefault();
          this.addCustomization();
        });

        // Add paid customization buttons
        const paidCustomizationButtons = this.querySelectorAll('[data-add-paid-customization]');
        paidCustomizationButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.addCustomization();
          });
        });

        // Customization Guide modal
        const guideModal = this.querySelector('[data-customization-guide-modal]');
        const guideTrigger = this.querySelector('[data-customization-guide-trigger]');
        const guideTrigger2 = this.querySelector('[data-customization-guide-trigger-2]');
        const guideCloseButtons = this.querySelectorAll('[data-guide-close]');

        if (guideTrigger && guideModal) {
          guideTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            guideModal.showModal();
          });
        }

        if (guideTrigger2 && guideModal) {
          guideTrigger2.addEventListener('click', (e) => {
            e.preventDefault();
            guideModal.showModal();
          });
        }

        if (guideModal && guideCloseButtons.length > 0) {
          guideCloseButtons.forEach(btn => {
            btn.addEventListener('click', () => {
              guideModal.close();
            });
          });
        }

        // Apply button - save and close modal
        this.applyButton?.addEventListener('click', (e) => {
          e.preventDefault();
          this.applyCustomizations();
        });

        // Close button - just close modal without clearing
        const clearAndCloseButton = this.querySelector('[data-clear-and-close]');
        // No action needed - data-popup-close on the button handles the close
        
        const closeButton = this.querySelector('[data-popup-close]');
        // No action needed - popup-component handles the close

        // Inject data before form submits - use capturing phase and don't prevent default
        if (this.form) {
          this.form.addEventListener('submit', (e) => {
            this.injectCustomizationData();
          }, true);
        }
        
        // Fix iOS scrolling when modal opens
        if (this.trigger && this.modal) {
          this.trigger.addEventListener('click', () => {
            // Force update status message when modal opens
            setTimeout(() => {
              console.log('[Modal Open] Forcing status message update');
              this.updateStatusMessage();
            }, 100);
            
            setTimeout(() => {
              // Find the scrollable container
              const scrollableContainer = this.modal.querySelector('[data-scroll-lock-scrollable]');
              if (scrollableContainer) {
                // Force scroll styles
                scrollableContainer.style.webkitOverflowScrolling = 'touch';
                scrollableContainer.style.overflowY = 'scroll';
                
                // Prevent touch events from propagating to prevent scroll lock interference
                scrollableContainer.addEventListener('touchstart', (e) => {
                  e.stopPropagation();
                }, { passive: true });
                
                scrollableContainer.addEventListener('touchmove', (e) => {
                  e.stopPropagation();
                }, { passive: true });
              }
            }, 100);
          });
        }
      }

      renderChoiceFields() {
        if (!this.choicesContainer) {
          return;
        }

        // Define all available choice options with their tag identifiers
        const allChoices = [
          {
            tag: 'custom-closures',
            label: 'Closures',
            options: ['Buttons', 'Snaps']
          },
          {
            tag: 'custom-waist-ties',
            label: 'Waist Ties',
            options: this.productTags.includes('custom-waist-no-tie') 
              ? ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)', 'No Ties']
              : ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)']
          },
          {
            tag: 'custom-pockets',
            label: 'Pockets',
            options: ['Side seam pockets', 'Patch pockets']
          }
        ];

        // Filter choices based on product tags
        const enabledChoices = allChoices.filter(choice => 
          this.productTags.includes(choice.tag)
        );

        if (enabledChoices.length === 0) {
          return; // No choice options for this product
        }

        const html = enabledChoices.map(choice => {
          // Get current saved value for this choice
          const savedValue = this.getSavedChoiceValue(choice.label);
          const isSaved = savedValue && savedValue !== '';
          
          return `
            <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 8px;" data-choice-row="${choice.label}">
              <div style="min-width: 70px; flex-shrink: 0;">
                <label style="font-weight: 500; color: #000; font-size: 0.9rem; display: block;">
                  ${choice.label}
                </label>
                <span data-selected-badge="${choice.label}" style="color: #059669; font-size: 0.75rem; font-weight: 600; display: ${isSaved ? 'block' : 'none'}; margin-top: 2px;">✓ Selected</span>
              </div>
              <select 
                name="properties[${choice.label}]"
                data-choice-select
                data-choice-label="${choice.label}"
                style="
                  flex: 1;
                  padding: 10px;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  font-size: 0.95rem;
                  background: white;
                  color: #000;
                "
              >
                <option value="">Standard</option>
                ${choice.options.map(opt => {
                  const escapedValue = opt.replace(/"/g, '&quot;');
                  return `<option value="${escapedValue}" ${savedValue === opt ? 'selected' : ''}>${opt}</option>`;
                }).join('')}
              </select>
            </div>
          `;
        }).join('');

        this.choicesContainer.innerHTML = html;
        
        // Add event listeners for immediate feedback
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        choiceSelects.forEach(select => {
          select.addEventListener('change', (e) => {
            const label = e.target.getAttribute('data-choice-label');
            const badge = this.querySelector(`[data-selected-badge="${label}"]`);
            if (badge) {
              badge.style.display = e.target.value ? 'block' : 'none';
            }
          });
        });
      }

      addCustomization() {
        if (this.customizations.length >= this.maxCustomizations) {
          this.showMessage(`Maximum ${this.maxCustomizations} customizations allowed`, 'error');
          return;
        }

        // Check if there are any unsaved customizations
        const hasUnsaved = this.customizations.some(c => !c.saved);
        if (hasUnsaved) {
          this.showMessage('Please save the current customization before adding another', 'error');
          return;
        }

        const index = this.customizations.length;

        const customization = {
          id: Date.now() + index,
          text: '',
          saved: false
        };

        this.customizations.push(customization);
        this.renderCustomization(customization, index);
        this.updateUI();
      }

      applyCustomizations() {
        // Filter out empty customizations
        const validCustomizations = this.customizations.filter(c => c.text.trim() !== '');
        
        // Check if any choice selections have been made
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        const hasChoiceSelections = Array.from(choiceSelects).some(select => select.value !== '');
        
        // Require either customization text OR choice selections
        if (validCustomizations.length === 0 && !hasChoiceSelections) {
          this.showMessage('Please add at least one customization or make a selection', 'error');
          return;
        }

        // Save current choice selections
        choiceSelects.forEach(select => {
          const label = select.getAttribute('data-choice-label');
          if (label && select.value) {
            this.setSavedChoiceValue(label, select.value);
          }
        });

        // Update customizations array
        this.customizations = validCustomizations;
        
        // Re-render to show only valid customizations
        this.clearAllContainers();
        this.customizations.forEach((c, i) => this.renderCustomization(c, i));
        
        // Re-render choices to show "✓ Saved" badges
        this.renderChoiceFields();
        
        // If editing an existing cart item, replace it
        if (this.editingCartItemKey) {
          this.replaceCartItem();
          return;
        }
        
        // Update trigger button
        this.updateTrigger();
        
        // Update status message to show customizations are applied
        this.updateStatusMessage();
        
        // Close modal using the dialog element directly
        if (this.modal) {
          this.modal.close();
        }
        
        this.showMessage('Customizations applied!', 'success');
      }

      clearAndClose() {
        // Clear all customizations and close modal
        this.customizations = [];
        this.clearAllContainers();
        
        // Clear saved choice selections
        this.savedChoices = {};
        
        // Reset all choice dropdowns to default "Select..." option
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        choiceSelects.forEach(select => {
          select.selectedIndex = 0; // Reset to first option (the "Select..." placeholder)
        });
        
        // Re-render choices to remove "✓ Saved" badges
        this.renderChoiceFields();
        
        // Update UI to reset total and buttons
        this.updateUI();
        
        // Reset trigger button
        this.updateTrigger();
        
        // Remove customization properties from form
        this.removeCustomizationInputs();
        
        // Remove fee from cart if it was added
        this.removeFeeFromCart();
        
        // Update status message
        this.updateStatusMessage();
        
        // Close modal
        if (this.modal) {
          this.modal.close();
        }
        
        this.showMessage('Customizations cleared', 'info');
      }

      updateStatusMessage() {
        const count = this.customizations.length;
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        const hasChoiceSelections = Array.from(choiceSelects).some(select => select.value !== '');
        
        console.log('[Status] Updating status messages - count:', count, 'hasChoiceSelections:', hasChoiceSelections);
        console.log('[Status] modalStatusMessage element:', this.modalStatusMessage);
        
        if (count > 0 || hasChoiceSelections) {
          // Get variant price - try multiple sources
          let productPriceCents = 0;
          
          // Method 1: Check selected variant from form
          const form = this.form;
          const selectedVariantId = form?.querySelector('[name="id"]')?.value;
          
          // Method 2: Try to get from button price display (if enabled in theme settings)
          const addToCartButton = document.querySelector('[data-add-to-cart]');
          const priceElement = addToCartButton?.querySelector('[data-product-price]');
          if (priceElement) {
            productPriceCents = parseInt(priceElement.getAttribute('data-price-cents') || '0', 10);
          }
          
          // Method 3: If still 0, try to get from product data or current variant
          if (productPriceCents === 0) {
            // Try getting from product form variant selector
            const variantSelector = form?.querySelector('select[name="id"], input[name="id"]:checked');
            if (variantSelector) {
              const selectedOption = variantSelector.tagName === 'SELECT' 
                ? variantSelector.options[variantSelector.selectedIndex]
                : variantSelector;
              
              // Try data attributes on the option/input
              productPriceCents = parseInt(selectedOption?.dataset?.price || '0', 10);
            }
          }
          
          // Method 4: Check window product object if available
          if (productPriceCents === 0 && window.ShopifyProductVariants && selectedVariantId) {
            const variant = window.ShopifyProductVariants.find(v => v.id == selectedVariantId);
            if (variant) {
              productPriceCents = variant.price;
            }
          }
          
          // Method 5: Use data attribute from custom element
          if (productPriceCents === 0 && this.dataset.productPriceCents) {
            productPriceCents = parseInt(this.dataset.productPriceCents, 10);
          }
          
          console.log('[Price] Final productPriceCents:', productPriceCents);
          
          const customizationTotal = this.calculateTotal();
          const customizationCents = Math.round(customizationTotal * 100);
          const totalCents = productPriceCents + customizationCents;
          
          // Format prices without currency symbols
          const formatPrice = (cents) => {
            const amount = (cents / 100).toFixed(2);
            return `${amount} ${this.currencyCode}`;
          };
          
          const itemPrice = formatPrice(productPriceCents);
          const customPrice = formatPrice(customizationCents);
          const totalPrice = formatPrice(totalCents);
          
          const messageHTML = `
            <strong>✓ Customizations applied!</strong><br>
            <span style="font-size: 0.9em; opacity: 0.9;">
              Item: ${itemPrice} + Customization: ${customPrice} = <strong>${totalPrice}</strong>
            </span>
          `;
          
          // Update both status messages (on page and in modal)
          this.statusMessage.innerHTML = messageHTML;
          this.statusMessage.style.display = 'block';
          
          if (this.modalStatusMessage) {
            console.log('[Status] Setting modal status message HTML');
            this.modalStatusMessage.innerHTML = messageHTML;
            this.modalStatusMessage.style.display = 'block';
          } else {
            console.warn('[Status] modalStatusMessage element not found!');
            // Try to find it again
            this.modalStatusMessage = this.querySelector('[data-modal-status-message]');
            if (this.modalStatusMessage) {
              console.log('[Status] Found modalStatusMessage on second try');
              this.modalStatusMessage.innerHTML = messageHTML;
              this.modalStatusMessage.style.display = 'block';
            }
          }
        } else {
          console.log('[Status] No customizations, hiding status messages');
          this.statusMessage.style.display = 'none';
          if (this.modalStatusMessage) {
            this.modalStatusMessage.style.display = 'none';
          }
        }
      }

      updateTrigger() {
        const count = this.customizations.length;
        const total = this.calculateTotal();
        
        // Check if any choice selections have been made
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        const hasChoiceSelections = Array.from(choiceSelects).some(select => select.value !== '');
        
        // Show customization status if there are text customizations OR choice selections
        if (count > 0 || hasChoiceSelections) {
          const totalCents = Math.round(total * 100);
          const formattedTotal = window.theme && window.theme.formatMoney 
            ? window.theme.formatMoney(totalCents, this.moneyFormat)
            : `${this.currencySymbol}${total.toFixed(2)}`;
          
          // Check if currency code is already in formatted string
          const totalWithCurrency = formattedTotal.includes(this.currencyCode) 
            ? formattedTotal 
            : `${formattedTotal} ${this.currencyCode}`;
          
          this.triggerText.textContent = `Edit Customizations (${totalWithCurrency})`;
          this.triggerCount.classList.add('hidden'); // Hide the separate count element
        } else {
          this.triggerText.textContent = 'Need Customization?';
          this.triggerCount.classList.add('hidden');
        }
        
        // Update status message as well
        this.updateStatusMessage();
        
        // Update Add to Cart button total
        this.updateAddToCartTotal();
      }
    
      // Update Add to Cart button to show product price + customization total
      updateAddToCartTotal() {
        const addToCartButton = document.querySelector('[data-add-to-cart]');
        if (!addToCartButton) return;
        
        // Get current product variant price (in cents)
        let productPriceCents = 0;
        
        // Try to get from data-product-price element (if theme setting enabled)
        const priceElement = addToCartButton.querySelector('[data-product-price]');
        if (priceElement) {
          productPriceCents = parseInt(priceElement.getAttribute('data-price-cents') || '0', 10);
        }
        
        // If not found, use the custom element's data attribute
        if (productPriceCents === 0 && this.dataset.productPriceCents) {
          productPriceCents = parseInt(this.dataset.productPriceCents, 10);
        }
        
        // Get customization total
        const customizationTotal = this.calculateTotal();
        const customizationCents = Math.round(customizationTotal * 100);
        
        // Calculate combined total
        const totalCents = productPriceCents + customizationCents;
        
        // Format the total price
        const formattedTotal = window.theme && window.theme.formatMoney 
          ? window.theme.formatMoney(totalCents, this.moneyFormat)
          : `${this.currencySymbol}${(totalCents / 100).toFixed(2)}`;
        
        // Check if currency code is already in formatted string
        const totalWithCurrency = formattedTotal.includes(this.currencyCode) 
          ? formattedTotal 
          : `${formattedTotal} ${this.currencyCode}`;
        
        // Update the price display if element exists
        if (priceElement) {
          priceElement.textContent = totalWithCurrency;
        } else {
          // If no price element in button, update the button text to show total
          const buttonText = addToCartButton.querySelector('[data-add-to-cart-text]');
          if (buttonText && customizationCents > 0) {
            // Get base button text (without any previous price)
            const baseText = buttonText.textContent.split('•')[0].trim();
            buttonText.textContent = `${baseText} • ${totalWithCurrency}`;
          }
        }
      }

      renderCustomization(customization, index) {
        const item = document.createElement('div');
        item.className = 'customization-item';
        item.dataset.customizationId = customization.id;
        
        // Check if this customization is saved (read-only) or editable
        const isSaved = customization.saved || false;
        const customizationPrice = index > 0 ? this.formatMoney(this.getPriceForCustomization(index + 1)) : '';

        item.innerHTML = `
          <div class="customization-item__wrapper">
            <div class="customization-item__header">
              <label class="customization-item__label">
                ${index === 0 ? 'Free Customization' : `Customization ${index + 1}`}
                ${isSaved ? '<span style="color: #059669; font-size: 0.75rem; font-weight: 600; margin-left: 8px;">✓ Saved</span>' : ''}
              </label>
              <div style="display: flex; gap: 8px;">
                ${isSaved ? `
                  <button type="button" class="customization-item__edit" data-edit-customization="${customization.id}" aria-label="Edit customization" style="
                    background: none;
                    border: none;
                    color: #666;
                    cursor: pointer;
                    padding: 4px 8px;
                    font-size: 0.8rem;
                    text-decoration: underline;
                  ">Edit</button>
                ` : ''}
                <button type="button" class="customization-item__remove" data-remove-customization="${customization.id}" aria-label="Remove customization" style="
                  background: none;
                  border: none;
                  color: #999;
                  cursor: pointer;
                  padding: 4px 8px;
                  font-size: 0.8rem;
                  text-decoration: underline;
                ">Delete</button>
              </div>
            </div>
            <div style="
              background: ${isSaved ? '#f9f9f9' : 'white'};
              padding: 12px;
              border: ${isSaved ? 'none' : '1px solid #ddd'};
              border-radius: 4px;
              color: #333;
              font-size: 0.9rem;
              line-height: 1.4;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              cursor: ${isSaved ? 'default' : 'pointer'};
              min-height: 44px;
              display: flex;
              align-items: center;
            " ${!isSaved ? `data-customization-text="${customization.id}"` : ''}>
              ${customization.text || (isSaved ? '' : 'Click to edit customization...')}
            </div>
            ${!isSaved ? `
              <textarea 
                class="customization-item__input"
                data-customization-input="${customization.id}"
                rows="3"
                maxlength="250"
                style="display: none; margin-top: 8px; font-size: 16px; padding: 10px; border: 1px solid #ddd; border-radius: 4px; width: 100%; font-family: inherit;"
              >${customization.text || ''}</textarea>
            ` : ''}
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
              <div class="customization-item__counter">
                <span data-char-count="${customization.id}">${customization.text?.length || 0}</span>/250
              </div>
              ${!isSaved ? `
                <button type="button" class="customization-item__save" data-save-customization="${customization.id}" style="
                  background: #000;
                  color: white;
                  border: none;
                  padding: 6px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 0.85rem;
                  font-weight: 500;
                  transition: background 0.2s;
                " onmouseover="this.style.background='#333'" onmouseout="this.style.background='#000'">Save Customization</button>
              ` : ''}
            </div>
          </div>
          ${index > 0 ? `
            <div style="margin-top: 8px; font-size: 0.8rem; color: #666; font-weight: 500;">
              Price: ${customizationPrice}
            </div>
          ` : ''}
        `;

        // Determine which container to use based on index
        const targetContainer = index === 0 
          ? this.querySelector('[data-free-customization]')
          : this.querySelector('[data-paid-customizations]');
        
        if (targetContainer) {
          targetContainer.appendChild(item);
        } else {
          // Fallback to default container
          this.container.appendChild(item);
        }

        // Bind events for this item
        const textarea = item.querySelector(`[data-customization-input="${customization.id}"]`);
        const textDisplay = item.querySelector(`[data-customization-text="${customization.id}"]`);
        const removeBtn = item.querySelector(`[data-remove-customization="${customization.id}"]`);
        const editBtn = item.querySelector(`[data-edit-customization="${customization.id}"]`);
        const saveBtn = item.querySelector(`[data-save-customization="${customization.id}"]`);
        const charCount = item.querySelector(`[data-char-count="${customization.id}"]`);
        
        // Click to expand/edit for unsaved customizations
        if (textDisplay && textarea) {
          textDisplay.addEventListener('click', () => {
            textDisplay.style.display = 'none';
            textarea.style.display = 'block';
            textarea.focus();
          });
          
          // Click outside to collapse
          textarea.addEventListener('blur', () => {
            if (!customization.saved) {
              setTimeout(() => {
                textDisplay.textContent = textarea.value || 'Click to edit customization...';
                textDisplay.style.display = 'flex';
                textarea.style.display = 'none';
              }, 200);
            }
          });
        }

        if (textarea) {
          textarea.addEventListener('input', (e) => {
            customization.text = e.target.value;
            charCount.textContent = e.target.value.length;
            if (textDisplay) {
              textDisplay.textContent = e.target.value || 'Click to edit customization...';
            }
            // Update save button state
            if (saveBtn) {
              saveBtn.disabled = !e.target.value.trim();
              saveBtn.style.opacity = e.target.value.trim() ? '1' : '0.5';
              saveBtn.style.cursor = e.target.value.trim() ? 'pointer' : 'not-allowed';
            }
            this.updateUI();
          });
        }

        removeBtn.addEventListener('click', () => {
          this.removeCustomization(customization.id);
        });

        if (editBtn) {
          editBtn.addEventListener('click', () => {
            customization.saved = false;
            this.clearAllContainers();
            this.customizations.forEach((c, i) => this.renderCustomization(c, i));
          });
        }

        if (saveBtn) {
          // Set initial disabled state
          saveBtn.disabled = !customization.text?.trim();
          saveBtn.style.opacity = customization.text?.trim() ? '1' : '0.5';
          saveBtn.style.cursor = customization.text?.trim() ? 'pointer' : 'not-allowed';
          
          saveBtn.addEventListener('click', () => {
            if (customization.text.trim()) {
              customization.saved = true;
              this.clearAllContainers();
              this.customizations.forEach((c, i) => this.renderCustomization(c, i));
            }
          });
        }

        // Focus the new textarea if not saved
        if (!isSaved) {
          requestAnimationFrame(() => textarea.focus());
        }
      }

      clearAllContainers() {
        // Clear the default container
        if (this.container) {
          this.container.innerHTML = '';
        }
        
        // Clear the free customization container
        const freeContainer = this.querySelector('[data-free-customization]');
        if (freeContainer) {
          freeContainer.innerHTML = '';
        }
        
        // Clear the paid customizations container
        const paidContainer = this.querySelector('[data-paid-customizations]');
        if (paidContainer) {
          paidContainer.innerHTML = '';
        }
      }

      removeCustomization(id) {
        const index = this.customizations.findIndex(c => c.id === id);
        if (index === -1) return;

        this.customizations.splice(index, 1);
        
        const item = this.container.querySelector(`[data-customization-id="${id}"]`);
        if (item) {
          item.style.opacity = '0';
          item.style.transform = 'translateX(-10px)';
          setTimeout(() => item.remove(), 200);
        }

        // Re-render all items to update numbering
        setTimeout(() => {
          this.clearAllContainers();
          this.customizations.forEach((c, i) => this.renderCustomization(c, i));
          this.updateUI();
        }, 200);
      }

      getSavedChoiceValue(label) {
        return this.savedChoices[label] || '';
      }

      setSavedChoiceValue(label, value) {
        this.savedChoices[label] = value;
      }

      updateUI() {
        const total = this.calculateTotal();
        const hasCustomizations = this.customizations.length > 0;
        const canAddMore = this.customizations.length < this.maxCustomizations;

        // Update total display in modal with correct currency
        if (this.totalAmount) {
          const totalCents = Math.round(total * 100);
          const formattedMoney = window.theme && window.theme.formatMoney 
            ? window.theme.formatMoney(totalCents, this.moneyFormat)
            : `${this.currencySymbol}${total.toFixed(2)}`;
          // Check if currency code is already in the formatted string
          if (formattedMoney.includes(this.currencyCode)) {
            this.totalAmount.textContent = formattedMoney;
          } else {
            this.totalAmount.textContent = `${formattedMoney} ${this.currencyCode}`;
          }
        }

        // Update free customization button state
        if (this.addButton) {
          this.addButton.disabled = !canAddMore;
          if (!canAddMore) {
            this.addButton.textContent = `Maximum ${this.maxCustomizations} customizations reached`;
          } else {
            // Show "Add Free Customization" only for first customization
            if (this.customizations.length === 0) {
              this.addButton.textContent = '+ Add Free Customization';
              this.addButton.style.display = 'block';
            } else {
              // Hide the button after first customization is added
              this.addButton.style.display = 'none';
            }
          }
        }

        // Update paid customization buttons state
        const paidButtons = this.querySelectorAll('[data-add-paid-customization]');
        paidButtons.forEach(button => {
          const customizationNum = parseInt(button.dataset.customizationNumber);
          const requiredCount = customizationNum - 1; // Need this many to enable this button
          
          // Check if this customization slot is already filled
          const alreadyHasThisCustomization = this.customizations.length >= customizationNum;
          
          if (alreadyHasThisCustomization) {
            // Already have this customization - hide the button
            button.style.display = 'none';
            button.nextElementSibling.style.display = 'none'; // Hide price too
          } else if (this.customizations.length === requiredCount) {
            // Can add this customization now
            button.disabled = false;
            button.style.opacity = '1';
            button.style.cursor = 'pointer';
            button.style.display = 'block';
            button.nextElementSibling.style.display = 'block'; // Show price
          } else if (this.customizations.length < requiredCount) {
            // Not ready for this customization yet
            button.disabled = true;
            button.style.opacity = '0.5';
            button.style.cursor = 'not-allowed';
            button.style.display = 'block';
            button.nextElementSibling.style.display = 'block'; // Show price
          }
        });
      }

      calculateTotal() {
        const count = this.customizations.length;
        
        // Fee Structure with FIXED prices per currency (no exchange rate conversion):
        // 
        // DRESSES:
        //   Customization 1: FREE
        //   Customization 2: Fixed amount per currency (tier2)
        //   Customization 3: Tier 2 + Fixed incremental amount (tier3)
        //
        // OTHER PRODUCTS:
        //   Customization 1: FREE
        //   Customization 2: Fixed amount per currency (tier2)
        //   Customization 3: Tier 2 + Fixed incremental amount (tier3)
        
        if (count <= 1) {
          return 0; // First customization is always free
        }
        
        if (count === 2) {
          return this.getFixedPrice('tier2'); // 2 customizations total price
        } else if (count === 3) {
          // 3 customizations = tier 2 price + tier 3 incremental
          return this.getFixedPrice('tier2') + this.getFixedPrice('tier3');
        }
        
        return 0; // Fallback (shouldn't reach here with max 3)
      }

      // Get price for a specific customization by count/index
      getPriceForCustomization(count) {
        if (count <= 1) return 0; // First is free
        
        // Return incremental cost for this specific customization
        // 2nd customization: tier2 price
        // 3rd customization: tier3 incremental price
        return count === 2 ? this.getFixedPrice('tier2') : this.getFixedPrice('tier3');
      }

      // Format money using Shopify's money format
      formatMoney(amount) {
        const amountInCents = Math.round(amount * 100);
        
        // Use theme's formatMoney which handles all format types (amount_with_comma_separator, etc.)
        if (window.theme && window.theme.formatMoney && this.moneyFormat) {
          return window.theme.formatMoney(amountInCents, this.moneyFormat);
        }
        
        // Fallback
        const formattedAmount = (amountInCents / 100).toFixed(2);
        return `${this.currencySymbol}${formattedAmount}`;
      }

      injectCustomizationData() {
        const validCustomizations = this.customizations.filter(c => c.text.trim());
        
        // Get choice selections
        const choiceSelects = this.querySelectorAll('[data-choice-select]');
        const hasChoices = Array.from(choiceSelects).some(select => select.value !== '');
        
        if (validCustomizations.length === 0 && !hasChoices) {
          // Remove any existing customization properties
          this.removeCustomizationInputs();
          return;
        }

        // Remove old inputs
        this.removeCustomizationInputs();

        // Add individual customizations as visible properties (shown in checkout)
        validCustomizations.forEach((c, i) => {
          const key = `Customization ${i + 1}`;
          this.createHiddenInput(`properties[${key}]`, c.text);
        });
        
        // Inject choice values as hidden inputs (because select elements are not inside the form)
        choiceSelects.forEach(select => {
          const label = select.getAttribute('data-choice-label');
          const value = select.value;
          if (label && value) {
            this.createHiddenInput(`properties[${label}]`, value);
          }
        });
        
        // Calculate and store pricing information (single source of truth)
        if (validCustomizations.length > 0) {
          const count = validCustomizations.length;
          const total = this.calculateTotal();
          const feeCents = Math.round(total * 100);
          
          // Store count as hidden property for cart recalculation
          this.createHiddenInput('properties[_customization_count]', count.toString());
        }
      }

      createHiddenInput(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.className = 'customization-property';
        this.form.appendChild(input);
      }

      removeCustomizationInputs() {
        this.form.querySelectorAll('.customization-property').forEach(input => input.remove());
      }

      showMessage(message, type = 'info') {
        if (!this.messageEl) return;
        
        this.messageEl.textContent = message;
        this.messageEl.className = `customizations-message visible ${type}`;
        
        setTimeout(() => {
          this.messageEl.classList.remove('visible');
        }, 3000);
      }
    });
  }

  /**
   * Cart Edit Handler - Standalone modal
   */
  document.addEventListener('click', (e) => {
    const editButton = e.target.closest('[data-edit-customizations]');
    if (!editButton) return;
    
    e.preventDefault();
    
    const cartItemKey = editButton.dataset.cartItemKey;
    const variantId = editButton.dataset.variantId;
    const productUrl = editButton.dataset.productUrl;
    
    // Fetch both cart data and product data
    Promise.all([
      fetch('/cart.js').then(r => r.json()),
      fetch(productUrl + '.js').then(r => r.json())
    ])
      .then(([cart, product]) => {
        const item = cart.items.find(i => i.key === cartItemKey);
        if (!item) return;
        
        console.log('Cart item properties:', item.properties);
        
        // Extract customizations - handle both old (_Customization) and new (Customization) formats
        const customizations = [];
        const choices = [];
        const choiceNames = ['Closures', 'Waist Ties', 'Pockets']; // Known choice property names
        
        Object.keys(item.properties || {}).forEach(key => {
          console.log('Property key:', key, 'Value:', item.properties[key]);
          // Match both "Customization X" and "_Customization X" (for backward compatibility)
          if (key.startsWith('Customization ') || key.startsWith('_Customization ')) {
            console.log('Found customization:', key, item.properties[key]);
            customizations.push({
              key: key,
              value: item.properties[key]
            });
          } else if (choiceNames.includes(key)) {
            // This is a choice property (Closures, Waist Ties, Pockets)
            console.log('Found choice:', key, item.properties[key]);
            choices.push({
              key: key,
              value: item.properties[key]
            });
          }
        });
        
        console.log('Extracted customizations:', customizations);
        console.log('Extracted choices:', choices);
        
        // Get product tags to determine which choice options are available
        const productTags = product.tags || [];
        
        // Open inline edit modal - pass cart object for currency
        openCartEditModal(item, customizations, choices, productTags, cartItemKey, variantId, cart);
      })
      .catch(err => console.error('Failed to load cart item:', err));
  });

  // Create and show inline edit modal
  function openCartEditModal(item, customizations, choices, productTags, cartItemKey, variantId, cart) {
    // Get pricing info from item properties
    const isDress = item.product_type && item.product_type.toLowerCase().includes('dress');
    
    // Get currency from cart object (correct source)
    const currencyCode = cart.currency || 'USD';
    const productType = item.product_type;
    
    // Fee variant IDs (no longer needed, using fixed pricing)
    const feeVariantId = isDress ? '56012000985467' : '56012001018235';
    
    // Define the setupModal function
    function setupModal() {
    
    // Helper to format money - use theme's formatMoney with currency code
    const formatMoney = (cents) => {
      // Currency symbols map
      const symbols = {
        USD: '$', CAD: '$', EUR: '€', GBP: '£', 
        AUD: '$', NOK: 'kr', SGD: '$', CHF: 'CHF'
      };
      
      const symbol = symbols[currencyCode] || currencyCode;
      const amount = (cents / 100).toFixed(2);
      
      // Format based on currency
      if (currencyCode === 'NOK') {
        return `${amount} ${symbol}`;
      } else if (currencyCode === 'CHF') {
        return `${symbol} ${amount}`;
      } else {
        return `${symbol}${amount} ${currencyCode}`;
      }
    };
    
    // Helper to get fixed pricing for buttons
    const getButtonPrice = (tier) => {
      const pricing = {
        dress: {
          USD: { tier2: 1000, tier3: 800 },
          CAD: { tier2: 1400, tier3: 1100 },
          EUR: { tier2: 900, tier3: 700 },
          GBP: { tier2: 800, tier3: 600 },
          AUD: { tier2: 1500, tier3: 1200 },
          NOK: { tier2: 10000, tier3: 8000 },
          SGD: { tier2: 1400, tier3: 1100 },
          CHF: { tier2: 900, tier3: 700 }
        },
        other: {
          USD: { tier2: 800, tier3: 400 },
          CAD: { tier2: 1100, tier3: 500 },
          EUR: { tier2: 700, tier3: 400 },
          GBP: { tier2: 600, tier3: 300 },
          AUD: { tier2: 1200, tier3: 600 },
          NOK: { tier2: 8000, tier3: 4000 },
          SGD: { tier2: 1100, tier3: 500 },
          CHF: { tier2: 700, tier3: 400 }
        }
      };
      
      const productType = isDress ? 'dress' : 'other';
      const prices = pricing[productType][currencyCode] || pricing[productType].USD;
      return prices[tier];
    };
    
    // Internal state - store customizations in an array
    let customizationsArray = customizations.map((c, i) => ({
      id: Date.now() + i,
      text: c.value,
      saved: true
    }));
    
    // Don't add empty customization - let the button do it (matches main modal)
    
    // Helper to calculate customization total using fixed currency pricing
    const calculateCustomizationTotal = (paidCount, isDress) => {
      // paidCount is the number of PAID customizations (not including free)
      if (paidCount === 0) return 0;
      
      const pricing = {
        dress: {
          USD: { tier2: 1000, tier3: 1800 },
          CAD: { tier2: 1400, tier3: 2500 },
          EUR: { tier2: 900, tier3: 1600 },
          GBP: { tier2: 800, tier3: 1400 },
          AUD: { tier2: 1500, tier3: 2700 },
          NOK: { tier2: 10000, tier3: 18000 },
          SGD: { tier2: 1400, tier3: 2500 },
          CHF: { tier2: 900, tier3: 1600 }
        },
        other: {
          USD: { tier2: 800, tier3: 1200 },
          CAD: { tier2: 1100, tier3: 1600 },
          EUR: { tier2: 700, tier3: 1100 },
          GBP: { tier2: 600, tier3: 900 },
          AUD: { tier2: 1200, tier3: 1800 },
          NOK: { tier2: 8000, tier3: 12000 },
          SGD: { tier2: 1100, tier3: 1600 },
          CHF: { tier2: 700, tier3: 1100 }
        }
      };
      
      const productType = isDress ? 'dress' : 'other';
      const prices = pricing[productType][currencyCode] || pricing[productType].USD;
      
      if (paidCount === 1) return prices.tier2;
      if (paidCount >= 2) return prices.tier3;
      return 0;
    };
    
    // Define all available choice options
    const allChoices = [
      {
        tag: 'custom-closures',
        label: 'Closures',
        options: ['Buttons', 'Snaps']
      },
      {
        tag: 'custom-waist-ties',
        label: 'Waist Ties',
        options: productTags.includes('custom-waist-no-tie') 
          ? ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)', 'No Ties']
          : ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)']
      },
      {
        tag: 'custom-pockets',
        label: 'Pockets',
        options: ['Side seam pockets', 'Patch pockets']
      }
    ];
    
    // Filter choices based on product tags
    const enabledChoices = allChoices.filter(choice => 
      productTags.includes(choice.tag)
    );
    
    // Render choice fields HTML
    const choiceFieldsHTML = enabledChoices.length > 0 ? `
      <div class="cart-edit-choices" style="margin-bottom: 24px;">
        ${enabledChoices.map(choice => {
          const currentValue = choices.find(c => c.key === choice.label)?.value || '';
          
          return `
            <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
              <div style="min-width: 70px; flex-shrink: 0;">
                <label style="font-weight: 500; color: #000; font-size: 0.9rem; display: block;">
                  ${choice.label}
                </label>
                <span data-choice-badge="${choice.label}" style="color: #059669; font-size: 0.75rem; font-weight: 600; display: ${currentValue ? 'block' : 'none'}; margin-top: 2px;">✓ Selected</span>
              </div>
              <select 
                class="cart-edit-choice"
                data-choice-name="${choice.label}"
                style="
                  flex: 1;
                  padding: 10px;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  font-size: 0.95rem;
                  background: white;
                  color: #000;
                "
              >
                <option value="">Standard</option>
                ${choice.options.map(opt => {
                  const escapedValue = opt.replace(/"/g, '&quot;');
                  return `<option value="${escapedValue}" ${opt === currentValue ? 'selected' : ''}>${opt}</option>`;
                }).join('')}
              </select>
            </div>
          `;
        }).join('')}
      </div>
    ` : '';
    
    // Create modal HTML
    const modalHTML = `
      <dialog class="cart-edit-modal" id="cart-edit-modal" data-scroll-lock-required style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 0;
        border: none;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.2);
        max-width: 500px;
        width: 90%;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        z-index: 10000;
        background: white;
        overflow: hidden;
        pointer-events: auto;
      ">
        <div class="cart-edit-modal-content" style="
          padding: 20px;
          overflow-y: scroll;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          flex: 1 1 auto;
          min-height: 0;
          position: relative;
          pointer-events: auto;
        " data-scroll-lock-scrollable ontouchmove="event.stopPropagation();">
          <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 24px;">
            <div>
              <h3 style="margin: 0 0 12px 0; font-size: 1.5rem; font-weight: 500; color: #000; text-align: center;">Customize Your Item</h3>
              <p style="margin: 0; color: #666; font-size: 0.9rem; line-height: 1.5; text-align: justify;">
                Your garment will be made exactly as described in the listing and shown in the product photos. However, you may request the following small customizations at no additional cost.
              </p>
            </div>
            <button type="button" class="cart-edit-close" onclick="const m=this.closest('dialog'); m.close(); setTimeout(() => m.remove(), 200);" style="
              background: none;
              border: none;
              font-size: 28px;
              cursor: pointer;
              padding: 0;
              line-height: 1;
              color: #999;
            ">&times;</button>
          </div>
          
          <!-- Price breakdown status message (at top) -->
          <div class="cart-edit-status" style="
            display: none;
            padding: 10px 16px;
            margin-bottom: 20px;
            border-radius: 4px;
            font-size: 0.9rem;
            text-align: center;
            background: #FBFAF5;
            color: #065f46;
            font-weight: 500;
          "></div>
          
          ${choiceFieldsHTML}
          
          <!-- Anything else section -->
          <div style="margin-bottom: 16px;">
            <h4 style="margin: 0 0 8px 0; font-size: 1rem; font-weight: 500; color: #000;">Anything else?</h4>
            <p style="margin: 0 0 16px 0; color: #666; font-size: 0.85rem; line-height: 1.5; text-align: justify;">
              Would you like one more adjustment that's not listed above? You may add one additional customization free of charge.
            </p>
          </div>
          
          <!-- FREE customization section (container) -->
          <div class="cart-edit-free" style="margin-bottom: 16px;"></div>
          
          <button 
            type="button"
            class="cart-edit-add-free-btn"
            style="
              background: white;
              border: 1px dashed #999;
              padding: 10px 16px;
              border-radius: 4px;
              cursor: pointer;
              width: 100%;
              margin-bottom: 16px;
              font-size: 0.9rem;
              color: #333;
              display: none;
            "
          >+ Add Free Customization</button>
          
          <div style="text-align: center; margin-bottom: 24px;">
            <button 
              type="button"
              class="cart-edit-guide"
              style="
                background: none;
                border: none;
                color: #000;
                text-decoration: underline;
                cursor: pointer;
                font-size: 0.85rem;
                padding: 0;
              "
            >Customization Guide</button>
          </div>
          
          <!-- Grouped section with background -->
          <div style="padding: 20px; background: #EBEBE1; border-radius: 8px; margin-top: 24px; margin-left: -32px; margin-right: -32px; padding-left: 32px; padding-right: 32px;">
            <div style="margin-bottom: 16px;">
              <h4 style="margin: 0 0 8px 0; font-size: 1rem; font-weight: 500; color: #000;">Need more than one customization?</h4>
              <p style="margin: 0; color: #666; font-size: 0.85rem; line-height: 1.5; text-align: justify;">
                If you'd like additional changes beyond the first free one, you can add up to <strong style="color: #000;">two extra</strong> customizations for an additional fee:
              </p>
            </div>
            
            <!-- PAID customizations section (container) -->
            <div class="cart-edit-paid"></div>
            
            <!-- Add 2nd Customization button -->
            <div data-btn-2nd-container style="margin-bottom: 16px;">
              <button 
                type="button"
                class="cart-edit-add-customization"
                style="
                  background: white;
                  border: 1px dashed #999;
                  padding: 10px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  width: 100%;
                  font-size: 0.9rem;
                  color: #333;
                "
              >+ Add 2nd Customization</button>
              <div style="text-align: center; margin-top: 8px; font-size: 0.8rem; color: #666; font-weight: 500;">
                Price: ${formatMoney(getButtonPrice('tier2'))}
              </div>
            </div>
            
            <!-- Add 3rd Customization button -->
            <div data-btn-3rd-container style="margin-bottom: 16px;">
              <button 
                type="button"
                class="cart-edit-add-customization"
                style="
                  background: white;
                  border: 1px dashed #999;
                  padding: 10px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  width: 100%;
                  font-size: 0.9rem;
                  color: #333;
                "
              >+ Add 3rd Customization</button>
              <div style="text-align: center; margin-top: 8px; font-size: 0.8rem; color: #666; font-weight: 500;">
                Price: ${formatMoney(getButtonPrice('tier3'))}
              </div>
            </div>
            
            <div style="text-align: center; margin-bottom: 20px;">
              <button 
                type="button"
                class="cart-edit-guide"
                style="
                  background: none;
                  border: none;
                  color: #000;
                  text-decoration: underline;
                  cursor: pointer;
                  font-size: 0.85rem;
                  padding: 0;
                "
              >Customization Guide</button>
            </div>
            
            <div style="margin-bottom: 20px; padding: 16px; background: white; border-radius: 4px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-weight: 500; color: #000;">Customization Total:</span>
                <span data-customization-total style="font-weight: 600; font-size: 1.1rem; color: #000;">${formatMoney(calculateCustomizationTotal(customizationsArray.length - 1, isDress))}</span>
              </div>
            </div>
            
            <div style="display: flex; gap: 12px; margin-bottom: 20px;">
            <button 
              type="button"
              class="cart-edit-clear"
              style="
                flex: 1;
                padding: 14px;
                border: 1px solid #ddd;
                background: white;
                border-radius: 4px;
                cursor: pointer;
                font-size: 1rem;
              "
            >Close</button>
            <button 
              type="button"
              class="cart-edit-apply"
              style="
                flex: 2;
                padding: 14px;
                border: none;
                background: #000;
                color: white;
                border-radius: 4px;
                cursor: pointer;
                font-size: 1rem;
                font-weight: 500;
              "
            >Save</button>
            </div>
            
            <p style="margin: 0; font-size: 0.85rem; color: #666; line-height: 1.5; text-align: center;">
              Please note that customized items are made especially for you and therefore can't be returned, refunded, or exchanged.
            </p>
          </div>
          
          <button type="button" class="cart-edit-add-free-placeholder" style="display: none;"></button>
          <button type="button" class="cart-edit-add" style="display: none;"></button>
        </div>
      </dialog>
      
      <!-- Customization Guide Modal -->
      <dialog 
        id="cart-edit-guide-modal"
        style="
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          max-width: 600px;
          border: none;
          border-radius: 8px;
          padding: 0;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          background: white;
          margin: 0;
          z-index: 10001;
        "
      >
        <div style="padding: 32px; background: white;">
          <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 24px;">
            <h3 style="margin: 0; font-size: 1.5rem; font-weight: 500; color: #000;">Customization Guide</h3>
            <button
              type="button"
              class="cart-guide-close"
              aria-label="Close"
              style="
                background: none;
                border: none;
                outline: none;
                font-size: 2rem;
                line-height: 1;
                cursor: pointer;
                padding: 0;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #666;
              "
              onmouseover="this.style.color='#000'"
              onmouseout="this.style.color='#666'"
            >&times;</button>
          </div>
          
          <div style="color: #333; font-size: 0.95rem; line-height: 1.6;">
            <ul style="margin: 0; padding-left: 20px;">
              <li style="margin-bottom: 10px;">Shortening or lengthening the garment (up to 20 cm / 7.9 in) — torso, waistline, or overall length</li>
              <li style="margin-bottom: 10px;">Shortening or lengthening sleeves</li>
              <li style="margin-bottom: 10px;">Adding or removing buttons or snaps</li>
              <li style="margin-bottom: 10px;">Minor adjustments to the neckline, collar, or armholes</li>
              <li style="margin-bottom: 10px;">Adding pockets</li>
              <li style="margin-bottom: 10px;">Adding small, thoughtful details that make the garment uniquely yours</li>
            </ul>
            
            <p style="margin: 16px 0 0 0; color: #666; font-size: 0.9rem;">
              Other small customizations may be possible depending on the garment's design and construction.
            </p>
          </div>
          
          <div style="margin-top: 24px;">
            <button 
              type="button"
              class="cart-guide-close"
              style="
                width: 100%;
                padding: 14px;
                border: none;
                background: #000;
                color: white;
                border-radius: 4px;
                cursor: pointer;
                font-size: 1rem;
                font-weight: 500;
              "
            >Close</button>
          </div>
        </div>
      </dialog>
    `;
    
    // Remove existing modals if any
    const existingModal = document.getElementById('cart-edit-modal');
    if (existingModal) existingModal.remove();
    const existingGuide = document.getElementById('cart-edit-guide-modal');
    if (existingGuide) existingGuide.remove();
    
    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    const modal = document.getElementById('cart-edit-modal');
    
    // Helper functions that reference modal - MUST be after modal creation
    const getPaidCustomizationCount = () => {
      const paidContainer = modal.querySelector('.cart-edit-paid');
      if (!paidContainer) return 0;
      return paidContainer.querySelectorAll('.cart-edit-item').length;
    };
    
    const updateTotal = () => {
      const totalSpan = modal.querySelector('[data-customization-total]');
      if (totalSpan) {
        const paidCount = getPaidCustomizationCount();
        totalSpan.textContent = formatMoney(calculateCustomizationTotal(paidCount, isDress));
      }
    };
    
    const updateAddButtons = () => {
      const addFreeBtn = modal.querySelector('.cart-edit-add-free-btn');
      const btn2ndContainer = modal.querySelector('[data-btn-2nd-container]');
      const btn3rdContainer = modal.querySelector('[data-btn-3rd-container]');
      
      // Show "Add Free Customization" button only if no customizations exist
      if (addFreeBtn) {
        addFreeBtn.style.display = customizationsArray.length === 0 ? 'block' : 'none';
      }
      
      const paidCount = customizationsArray.length - 1;
      
      if (btn2ndContainer) {
        btn2ndContainer.style.display = paidCount >= 1 ? 'none' : 'block';
      }
      
      if (btn3rdContainer) {
        btn3rdContainer.style.display = paidCount >= 2 ? 'none' : 'block';
      }
    };
    
    const renderAllCustomizations = () => {
      const freeContainer = modal.querySelector('.cart-edit-free');
      const paidContainer = modal.querySelector('.cart-edit-paid');
      if (freeContainer) freeContainer.innerHTML = '';
      if (paidContainer) paidContainer.innerHTML = '';
      
      customizationsArray.forEach((customization, index) => {
        const isFree = index === 0;
        const targetContainer = isFree ? freeContainer : paidContainer;
        if (!targetContainer) return;
        
        const item = document.createElement('div');
        item.className = 'cart-edit-item';
        item.setAttribute('data-customization-id', customization.id);
        item.setAttribute('data-saved', customization.saved);
        item.style.cssText = 'margin-bottom: 16px; position: relative;';
        
        const priceText = !isFree ? `<div style="margin-top: 8px; font-size: 0.8rem; color: #666; font-weight: 500;">
          Price: ${index === 1 ? formatMoney(getButtonPrice('tier2')) : formatMoney(getButtonPrice('tier3'))}
        </div>` : '';
        
        item.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <label style="font-weight: 500; font-size: 0.875rem; color: #000;">
              ${isFree ? 'Free Customization' : `Customization ${index + 1}`}
              <span style="color: #059669; font-size: 0.75rem; font-weight: 600; margin-left: 8px; display: ${customization.saved ? 'inline' : 'none'};">✓ Saved</span>
            </label>
            <div style="display: flex; gap: 8px;">
              <button type="button" class="cart-edit-edit" data-edit-id="${customization.id}" style="
                background: none;
                border: none;
                color: #666;
                cursor: pointer;
                padding: 4px 8px;
                font-size: 0.8rem;
                text-decoration: underline;
                display: ${customization.saved ? 'inline' : 'none'};
              " aria-label="Edit">Edit</button>
              <button type="button" class="cart-edit-delete" data-delete-id="${customization.id}" style="
                background: none;
                border: none;
                color: #999;
                cursor: pointer;
                padding: 4px 8px;
                font-size: 0.8rem;
                text-decoration: underline;
              " aria-label="Delete">Delete</button>
            </div>
          </div>
          
          <div class="cart-edit-text-display" data-text-display="${customization.id}" style="
            background: ${customization.saved ? '#f9f9f9' : 'white'};
            padding: 12px;
            border: ${customization.saved ? 'none' : '1px solid #ddd'};
            border-radius: 4px;
            color: #333;
            font-size: 0.9rem;
            line-height: 1.4;
            min-height: 44px;
            display: ${customization.saved ? 'flex' : 'none'};
            align-items: center;
          ">${customization.text}</div>
          
          <textarea 
            class="cart-edit-input" 
            data-customization-id="${customization.id}"
            rows="3"
            maxlength="250"
            style="
              display: ${customization.saved ? 'none' : 'block'};
              width: 100%;
              padding: 10px;
              margin-top: 8px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 16px;
              font-family: inherit;
              resize: vertical;
              background: white;
              color: #000;
            "
          >${customization.text}</textarea>
          
          <div style="display: ${customization.saved ? 'none' : 'flex'}; justify-content: space-between; align-items: center; margin-top: 8px;" data-edit-controls="${customization.id}">
            <div style="font-size: 0.75rem; color: #999;">
              <span data-char-count="${customization.id}">${customization.text.length}</span>/250
            </div>
            <button type="button" class="cart-edit-save" data-save-id="${customization.id}" style="
              background: #000;
              color: white;
              border: none;
              padding: 6px 16px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 0.85rem;
              font-weight: 500;
            " onmouseover="this.style.background='#333'" onmouseout="this.style.background='#000'">Save Customization</button>
          </div>
          ${priceText}
        `;
        
        targetContainer.appendChild(item);
        
        const textarea = item.querySelector(`[data-customization-id="${customization.id}"]`);
        const charCount = item.querySelector(`[data-char-count="${customization.id}"]`);
        const editBtn = item.querySelector(`[data-edit-id="${customization.id}"]`);
        const saveBtn = item.querySelector(`[data-save-id="${customization.id}"]`);
        const deleteBtn = item.querySelector(`[data-delete-id="${customization.id}"]`);
        const textDisplay = item.querySelector(`[data-text-display="${customization.id}"]`);
        const editControls = item.querySelector(`[data-edit-controls="${customization.id}"]`);
        const savedBadge = item.querySelector('span[style*="color: #059669"]');
        
        if (textarea && charCount) {
          textarea.addEventListener('input', () => {
            charCount.textContent = textarea.value.length;
            customization.text = textarea.value;
          });
        }
        
        if (editBtn && textarea && textDisplay && editControls) {
          editBtn.addEventListener('click', () => {
            customization.saved = false;
            textDisplay.style.display = 'none';
            textarea.style.display = 'block';
            editControls.style.display = 'flex';
            editBtn.style.display = 'none';
            if (savedBadge) savedBadge.style.display = 'none';
            textarea.focus();
          });
        }
        
        if (saveBtn && textarea && textDisplay && editControls) {
          saveBtn.addEventListener('click', () => {
            if (customization.text.trim()) {
              customization.saved = true;
              textDisplay.textContent = customization.text;
              textDisplay.style.display = 'flex';
              textDisplay.style.background = '#f9f9f9';
              textDisplay.style.border = 'none';
              textarea.style.display = 'none';
              editControls.style.display = 'none';
              if (editBtn) editBtn.style.display = 'inline';
              if (savedBadge) savedBadge.style.display = 'inline';
            }
          });
        }
        
        if (deleteBtn) {
          deleteBtn.addEventListener('click', () => {
            const idx = customizationsArray.findIndex(c => c.id === customization.id);
            if (idx !== -1) {
              // Remove any customization (including free one)
              customizationsArray.splice(idx, 1);
            }
            renderAllCustomizations();
            updateAddButtons();
            updateTotal();
          });
        }
      });
      
      updateAddButtons();
    };
    
    // Add hover effects
    const style = document.createElement('style');
    style.textContent = `
      .cart-edit-close:hover { color: #333 !important; }
      .cart-edit-input:focus { border-color: #999 !important; outline: none; }
      .cart-edit-add:hover { background: #f5f5f5 !important; border-color: #999 !important; }
      .cart-edit-clear:hover { background: #f5f5f5 !important; }
      .cart-edit-guide:hover { background: #f5f5f5 !important; }
      .cart-edit-apply:hover { background: #333 !important; }
      #cart-edit-modal::backdrop { background: rgba(0, 0, 0, 0.5); }
    `;
    document.head.appendChild(style);
    
    // Event listeners - attach BEFORE showing modal
    const closeBtn = modal.querySelector('.cart-edit-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        modal.close();
        setTimeout(() => modal.remove(), 200);
      });
    }
    
    // Close button (without saving)
    const clearBtn = modal.querySelector('.cart-edit-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        modal.close();
        setTimeout(() => modal.remove(), 200);
      });
    }
    
    // Guide buttons
    modal.querySelectorAll('.cart-edit-guide').forEach(btn => {
      btn.addEventListener('click', () => {
        const guideModal = document.getElementById('cart-edit-guide-modal');
        if (guideModal) {
          guideModal.showModal();
        }
      });
    });
    
    // Guide modal close buttons
    const guideModal = document.getElementById('cart-edit-guide-modal');
    if (guideModal) {
      guideModal.querySelectorAll('.cart-guide-close').forEach(btn => {
        btn.addEventListener('click', () => {
          guideModal.close();
        });
      });
    }
    
    // Choice select change handlers - show checkmark
    modal.querySelectorAll('.cart-edit-choice').forEach(select => {
      select.addEventListener('change', (e) => {
        const choiceName = e.target.dataset.choiceName;
        const badge = modal.querySelector(`[data-choice-badge="${choiceName}"]`);
        if (badge) {
          badge.style.display = e.target.value ? 'block' : 'none';
        }
      });
    });
    
    // Save changes
    modal.querySelector('.cart-edit-apply').addEventListener('click', () => {
      const choiceSelects = modal.querySelectorAll('.cart-edit-choice');
      const properties = {};
      
      // Get choice values
      choiceSelects.forEach(select => {
        const name = select.dataset.choiceName;
        const value = select.value;
        if (value) {
          properties[name] = value;
        }
      });
      
      // Get customization text values from customizationsArray
      const validCustomizations = customizationsArray.filter(c => c.text.trim());
      
      // Add individual customizations as visible properties
      validCustomizations.forEach((c, index) => {
        properties[`Customization ${index + 1}`] = c.text.trim();
      });
      
      // Calculate and store pricing (single source of truth)
      if (validCustomizations.length > 0) {
        // Use currencyCode from outer scope (already from cart.currency)
        const isDress = productType.toLowerCase().includes('dress');
        
        // Calculate fee using pricing table
        const count = validCustomizations.length;
        let feeCents = 0;
        
        if (count === 2) {
          if (isDress) {
            const dressTier2 = { USD: 1000, CAD: 1400, EUR: 900, GBP: 800, AUD: 1500, NOK: 10000, SGD: 1400, CHF: 900 };
            feeCents = dressTier2[currencyCode] || 1000;
          } else {
            const otherTier2 = { USD: 800, CAD: 1100, EUR: 700, GBP: 600, AUD: 1200, NOK: 8000, SGD: 1100, CHF: 700 };
            feeCents = otherTier2[currencyCode] || 800;
          }
        } else if (count === 3) {
          if (isDress) {
            const dressTier3 = { USD: 1800, CAD: 2500, EUR: 1600, GBP: 1400, AUD: 2700, NOK: 18000, SGD: 2500, CHF: 1600 };
            feeCents = dressTier3[currencyCode] || 1800;
          } else {
            const otherTier3 = { USD: 1200, CAD: 1600, EUR: 1100, GBP: 900, AUD: 1800, NOK: 12000, SGD: 1600, CHF: 1100 };
            feeCents = otherTier3[currencyCode] || 1200;
          }
        }
        
        properties['_customization_count'] = count.toString();
      }
      
      // Show loading state
      const saveBtn = modal.querySelector('.cart-edit-apply');
      saveBtn.textContent = 'Updating...';
      saveBtn.disabled = true;
      
      // Replace cart item
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cartItemKey, quantity: 0 })
      })
      .then(() => {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: properties
          })
        });
      })
      .then(() => {
        modal.close();
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(err => {
        console.error('Failed to update:', err);
        const errorMsg = document.createElement('div');
        errorMsg.style.cssText = 'color: #d32f2f; font-size: 0.85rem; margin: 12px 0; text-align: center; padding: 12px; background: #ffebee; border-radius: 4px;';
        errorMsg.textContent = 'Failed to update customizations. Please try again.';
        saveBtn.parentElement.insertBefore(errorMsg, saveBtn);
        saveBtn.textContent = 'Save';
        saveBtn.disabled = false;
        setTimeout(() => errorMsg.remove(), 5000);
      });
    });
    
    // Update price breakdown status
    updateCartEditStatus(modal, item, customizations.length, currencyCode);
    
    // Initial render of all customizations
    renderAllCustomizations();
    updateAddButtons();
    
    // Setup "Add Free Customization" button
    const addFreeBtn = modal.querySelector('.cart-edit-add-free-btn');
    if (addFreeBtn) {
      addFreeBtn.addEventListener('click', () => {
        const newCustomization = {
          id: Date.now(),
          text: '',
          saved: false
        };
        customizationsArray.push(newCustomization);
        renderAllCustomizations();
        updateAddButtons();
        updateTotal();
        // Focus textarea
        setTimeout(() => {
          const textarea = modal.querySelector('.cart-edit-free textarea');
          if (textarea) textarea.focus();
        }, 50);
      });
    }
    
    // Setup add customization button handlers
    modal.querySelectorAll('.cart-edit-add-customization').forEach(btn => {
      btn.addEventListener('click', () => {
        const hasUnsaved = customizationsArray.some(c => !c.saved);
        if (hasUnsaved) {
          alert('Please save the current customization before adding another');
          return;
        }
        
        const newCustomization = {
          id: Date.now(),
          text: '',
          saved: false
        };
        customizationsArray.push(newCustomization);
        renderAllCustomizations();
        updateTotal();
      });
    });
    
    // Show modal after all setup is complete
    modal.showModal();
    } // End setupModal
    
    // Call setupModal to initialize and show the modal
    setupModal();
  }

  // Helper function to update cart edit modal status message
  function updateCartEditStatus(modal, item, customizationCount, cartCurrency) {
    const statusEl = modal.querySelector('.cart-edit-status');
    if (!statusEl || customizationCount === 0) {
      if (statusEl) statusEl.style.display = 'none';
      return;
    }
    
    // Calculate customization fee using fixed currency pricing
    const productType = item.product_type || '';
    const isDress = productType.toLowerCase().includes('dress');
    
    // Use cart currency passed from modal
    const currencyCode = cartCurrency || 'USD';
    
    // Fixed pricing structure (incremental, in cents)
    const pricing = {
      dress: {
        USD: { tier2: 1000, tier3: 800 },
        CAD: { tier2: 1400, tier3: 1100 },
        EUR: { tier2: 900, tier3: 700 },
        GBP: { tier2: 800, tier3: 600 },
        AUD: { tier2: 1500, tier3: 1200 },
        NOK: { tier2: 10000, tier3: 8000 },
        SGD: { tier2: 1400, tier3: 1100 },
        CHF: { tier2: 900, tier3: 700 }
      },
      other: {
        USD: { tier2: 800, tier3: 400 },
        CAD: { tier2: 1100, tier3: 500 },
        EUR: { tier2: 700, tier3: 400 },
        GBP: { tier2: 600, tier3: 300 },
        AUD: { tier2: 1200, tier3: 600 },
        NOK: { tier2: 8000, tier3: 4000 },
        SGD: { tier2: 1100, tier3: 500 },
        CHF: { tier2: 700, tier3: 400 }
      }
    };
    
    const productTypePricing = isDress ? 'dress' : 'other';
    const prices = pricing[productTypePricing][currencyCode] || pricing[productTypePricing].USD;
    
    let customizationFeeCents = 0;
    if (customizationCount === 2) {
      customizationFeeCents = prices.tier2;
    } else if (customizationCount >= 3) {
      customizationFeeCents = prices.tier2 + prices.tier3;
    }
    
    // Get base price by subtracting calculated fee from cart item price
    // (in case fee was added via other methods or is in the variant price)
    const productPrice = item.price - customizationFeeCents;
    const totalPrice = item.price;
    
    // Format prices without currency symbols
    const formatPrice = (cents) => {
      const amount = (cents / 100).toFixed(2);
      return `${amount} ${currencyCode}`;
    };
    
    const itemPrice = formatPrice(productPrice);
    const customPrice = formatPrice(customizationFeeCents);
    const totalPriceFormatted = formatPrice(totalPrice);
    
    statusEl.innerHTML = `
      <strong>✓ Customizations applied!</strong><br>
      <span style="font-size: 0.9em; opacity: 0.9;">
        Item: ${itemPrice} + Customization: ${customPrice} = <strong>${totalPriceFormatted}</strong>
      </span>
    `;
    statusEl.style.display = 'block';
  }

})();
